/**
 * The filename a download response names in its Content-Disposition header
 * (2026-10 browser QA, payroll #22: the salary slip saved as
 * salary-slip-2026-09-7.pdf — the internal id — because the page's own
 * fallback name overrode the server's salary-slip-2026-09-<employee
 * code>.pdf). Pure.
 *
 * RFC 6266: an RFC 5987 `filename*=UTF-8''…` wins over a plain
 * `filename="…"`. Path separators are stripped. null when the header is
 * absent (e.g. the server did not expose it cross-origin) or names nothing.
 */
export function filenameFromDisposition(header: string | null | undefined): string | null {
  if (!header) return null;
  const star = /filename\*\s*=\s*([^']*)'[^']*'([^;]+)/i.exec(header);
  let name: string | null = null;
  if (star) {
    try {
      name = decodeURIComponent(star[2].trim().replace(/^"|"$/g, ""));
    } catch {
      name = null;
    }
  }
  if (!name) {
    const plain = /filename\s*=\s*("([^"]*)"|[^;]+)/i.exec(header);
    if (plain) name = (plain[2] ?? plain[1]).trim();
  }
  if (!name) return null;
  const safe = name.replace(/[/\\]/g, "_").trim();
  return safe === "" ? null : safe;
}

/**
 * The blob a download API returns, carrying the server's filename (2026-10
 * audit Q27). A blob: URL has no response headers, so a link without a
 * `download` name either NAVIGATES the tab to the file (a PDF unloads the
 * app) or saves it under the blob's UUID with no extension — the server's
 * Content-Disposition never applies. The API layer therefore reads the
 * header here and returns a File (a Blob with a name) when it names one;
 * saveBlobAs (lib/save-blob.ts) saves under that name, else the caller's
 * fallback. `File` exists in browsers and in Node >= 20 (the unit runner).
 */
export function namedBlob(data: Blob, contentDisposition: unknown): Blob {
  const name = filenameFromDisposition(typeof contentDisposition === "string" ? contentDisposition : null);
  if (!name) return data;
  return new File([data], name, { type: data.type });
}

/** The name namedBlob attached to a blob, or null. */
export function blobFilename(blob: Blob): string | null {
  const name = (blob as Partial<File>).name;
  return typeof name === "string" && name.trim() !== "" ? name : null;
}

/** File extension (with the dot) for a download's media type; "" when the
 *  type is unknown (the caller's fallback name then has none). */
export function extensionForType(type: string): string {
  const t = type.split(";")[0].trim().toLowerCase();
  switch (t) {
    case "application/pdf":
      return ".pdf";
    case "text/csv":
      return ".csv";
    case "text/plain":
      return ".txt";
    case "application/json":
      return ".json";
    case "application/xml":
    case "text/xml":
      return ".xml";
    case "application/msword":
      return ".doc";
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return ".docx";
    case "image/jpeg":
      return ".jpg";
    case "image/png":
      return ".png";
    case "image/webp":
      return ".webp";
    default:
      return "";
  }
}

/** A filesystem-safe filename stem ("Bonus Form D" → "bonus-form-d"). */
export function filenameStem(label: string): string {
  const stem = label
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return stem || "download";
}

/** The name a blob is saved under: the server's (namedBlob), else the
 *  fallback, else "download" — never empty (an empty `download` attribute
 *  is what made the link navigate instead of saving). */
export function downloadName(blob: Blob, fallback: string): string {
  return blobFilename(blob) ?? (fallback.trim() || "download");
}
